// Reconciled tab — audit table of reconciled match groups, organised by
// category per SPEC §10.5:
//
//   Vendor invoices  — id_exact / fuzzy_high / manual (no set_aside_reason)
//   Intercompany     — intercompany
//   Clearings        — clearing_pair / clearing_orphan (amber pill on orphans)
//   Accruals         — accrual_reversal / accrual_orphan (amber pill on orphans)
//   Set aside        — manual w/ set_aside_reason
//
// Sections fetch independently so each can paginate without affecting the
// others. The page-level component owns the section state.

import React, { useEffect, useState, useCallback } from 'react';
import { TOKENS, UI_FONT, NUM_STYLE, CODE_STYLE, fmtMoney, CATEGORY_HINTS } from './tokens';
import { Mono, MoneyCell, Tone, Icon, styles } from './primitives';
import { fetchReconciled } from './api';

// Section totals use USD because budget_impact_usd is the canonical
// scalar — we don't try to break it out per currency in the header.
const fmtUsd = (n) => fmtMoney(n, 'USD');

const SECTIONS = [
  {
    id: 'vendor_invoice',
    label: 'Vendor invoices',
    categories: 'id_exact,fuzzy_high,manual',
    hint: CATEGORY_HINTS.vendor_invoice,
  },
  {
    id: 'intercompany',
    label: 'Intercompany',
    categories: 'intercompany',
    hint: CATEGORY_HINTS.intercompany,
  },
  {
    id: 'clearing',
    label: 'Clearings',
    categories: 'clearing_pair,clearing_orphan',
    hint: CATEGORY_HINTS.clearing,
  },
  {
    id: 'accrual',
    label: 'Accruals',
    categories: 'accrual_reversal,accrual_orphan',
    hint: CATEGORY_HINTS.accrual,
  },
];

const ORPHAN_CATEGORIES = new Set(['clearing_orphan', 'accrual_orphan']);
const PAGE_SIZE = 50;

export default function ReconciledView({ period, onUnreconcile, refreshKey }) {
  return (
    <div style={{ flex: '1 1 auto', overflow: 'auto', padding: '14px 18px 40px' }}>
      {SECTIONS.map((s) => (
        <ReconciledSection
          key={s.id}
          period={period}
          label={s.label}
          hint={s.hint}
          categories={s.categories}
          onUnreconcile={onUnreconcile}
          refreshKey={refreshKey}
        />
      ))}
    </div>
  );
}

function ReconciledSection({ period, label, hint, categories, onUnreconcile, refreshKey }) {
  const [matches, setMatches] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalUsd, setTotalUsd] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchReconciled(period, {
        limit: PAGE_SIZE,
        offset,
        category: categories,
      });
      setMatches(res.data?.matches || []);
      setTotal(res.data?.total || 0);
      setTotalUsd(Number(res.data?.total_amount_usd) || 0);
    } catch (err) {
      if (err.response?.status === 404) {
        // Backend isn't ready or empty period; show empty state.
        setMatches([]);
        setTotal(0);
        setTotalUsd(0);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to load');
      }
    } finally {
      setLoading(false);
    }
  }, [period, offset, categories]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Hide whole section if it's empty and we're past loading.
  if (!loading && !error && matches.length === 0) {
    return null;
  }

  const pages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE);

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        onClick={() => setCollapsed((c) => !c)}
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          marginBottom: 6,
          paddingBottom: 6,
          borderBottom: `1px solid ${TOKENS.border}`,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignSelf: 'center',
            color: TOKENS.ink3,
            transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
            transition: 'transform 120ms ease',
          }}
        >
          <Icon d="M6 9l6 6 6-6" size={11} sw={2.5} />
        </span>
        <Mono size={10.5} color={TOKENS.ink3}>
          {label.toUpperCase()}
        </Mono>
        {hint && (
          <span
            title={hint}
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              color: TOKENS.ink3,
              cursor: 'help',
            }}
          >
            <Icon d="M12 16v-4m0-4h.01M12 21a9 9 0 100-18 9 9 0 000 18z" size={13} sw={1.8} />
          </span>
        )}
        <span
          style={{
            ...NUM_STYLE,
            fontSize: 16,
            fontWeight: 500,
            color: TOKENS.ok,
          }}
        >
          {fmtUsd(totalUsd)}
        </span>
        <span style={{ fontSize: 11.5, color: TOKENS.ink3 }}>
          {total.toLocaleString()} match{total === 1 ? '' : 'es'}
          {!collapsed && total > matches.length && ` · showing ${matches.length}`}
        </span>
      </div>

      {!collapsed && error && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 7,
            background: 'oklch(0.96 0.04 25)',
            border: `1px solid ${TOKENS.danger}`,
            color: TOKENS.danger,
            fontSize: 11.5,
            fontFamily: UI_FONT,
          }}
        >
          {error}
        </div>
      )}

      {!collapsed && !error && (
        <table style={styles.tableBase}>
          <thead>
            <tr>
              <th style={styles.thStyle}>SAP doc</th>
              <th style={{ ...styles.thStyle, textAlign: 'right' }}>SAP amount</th>
              <th style={styles.thStyle}>Invoice</th>
              <th style={styles.thStyle}>Vendor</th>
              <th style={{ ...styles.thStyle, textAlign: 'right' }}>Invoice amount</th>
              <th style={styles.thStyle}>Reconciled</th>
              <th style={styles.thStyle}>By</th>
              <th style={{ ...styles.thStyle, width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {matches.map((m) => {
              const sap = m.sap_transaction || {};
              const inv = m.invoice || null;
              const isOrphan = ORPHAN_CATEGORIES.has(m.match_category);
              const isAuto = m.matched_by === 'system';
              const isSetAside = m.set_aside_reason != null;
              return (
                <tr key={m.id} className="recon-row" style={{ background: TOKENS.panel }}>
                  <td style={styles.tdStyle}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Mono style={CODE_STYLE}>{sap.sap_document_number || '—'}</Mono>
                      {isOrphan && (
                        <Tone tone="soft">
                          Orphan
                        </Tone>
                      )}
                      {isSetAside && (
                        <Tone tone="info">Set aside</Tone>
                      )}
                    </div>
                  </td>
                  <td style={{ ...styles.tdStyle, textAlign: 'right' }}>
                    <MoneyCell
                      value={Number(sap.amount || 0)}
                      currency={sap.currency || 'EUR'}
                    />
                  </td>
                  <td style={styles.tdStyle}>
                    {inv ? (
                      <Mono style={CODE_STYLE}>{inv.invoice_number}</Mono>
                    ) : (
                      <Mono color={TOKENS.ink3}>—</Mono>
                    )}
                  </td>
                  <td style={styles.tdStyle}>
                    {inv?.vendor || sap.inferred_vendor || (
                      <span style={{ color: TOKENS.ink3 }}>—</span>
                    )}
                  </td>
                  <td style={{ ...styles.tdStyle, textAlign: 'right' }}>
                    {inv ? (
                      <MoneyCell
                        value={Number(inv.amount || 0)}
                        currency={inv.currency || 'EUR'}
                      />
                    ) : (
                      <Mono color={TOKENS.ink3}>—</Mono>
                    )}
                  </td>
                  <td style={styles.tdStyle}>
                    <Mono size={10.5} color={TOKENS.ink2}>
                      {formatDate(m.created_at)}
                    </Mono>
                    {isSetAside && (
                      <div
                        style={{
                          fontSize: 10.5,
                          color: TOKENS.ink3,
                          marginTop: 2,
                          maxWidth: 220,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={m.set_aside_reason}
                      >
                        “{m.set_aside_reason}”
                      </div>
                    )}
                  </td>
                  <td style={styles.tdStyle}>
                    {isAuto ? (
                      <Tone
                        tone="info"
                        icon={<Icon d="M12 8v8 M8 12h8" size={9} sw={3} />}
                      >
                        Auto
                      </Tone>
                    ) : (
                      <span style={{ fontSize: 11 }}>{m.matched_by_name || 'Manual'}</span>
                    )}
                  </td>
                  <td style={styles.tdStyle}>
                    {canUnreconcile(m) && (
                      <button
                        onClick={() => onUnreconcile(m.linked_match_group_id || m.id)}
                        style={{
                          ...styles.ghostBtn,
                          padding: '3px 8px',
                          fontSize: 11,
                        }}
                      >
                        Unreconcile
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {loading && matches.length === 0 && (
              <tr>
                <td colSpan={8} style={{ ...styles.tdStyle, textAlign: 'center', color: TOKENS.ink3 }}>
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {!collapsed && pages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginTop: 8,
            justifyContent: 'flex-end',
          }}
        >
          <button
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={currentPage === 0}
            style={{
              ...styles.ghostBtn,
              opacity: currentPage === 0 ? 0.5 : 1,
              cursor: currentPage === 0 ? 'default' : 'pointer',
            }}
          >
            ← Prev
          </button>
          <Mono size={11} color={TOKENS.ink3}>
            Page {currentPage + 1} / {pages}
          </Mono>
          <button
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={currentPage >= pages - 1}
            style={{
              ...styles.ghostBtn,
              opacity: currentPage >= pages - 1 ? 0.5 : 1,
              cursor: currentPage >= pages - 1 ? 'default' : 'pointer',
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

function canUnreconcile(match) {
  // Vendor invoices and set-aside rows can be unreconciled. Intercompany,
  // clearings, and accruals are terminal in v1 (Phase D will add a
  // re-categorize affordance). Per SPEC §5 "Per-section rows".
  if (match.set_aside_reason) return true;
  return ['id_exact', 'fuzzy_high', 'manual'].includes(match.match_category);
}

function formatDate(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  } catch {
    return s;
  }
}
